import i18next from '../../vendor/i18next/i18next.esm.js';
import { S } from '../app/app_state.js?v=20260817c';
import { AppRuntime } from '../app/app_runtime.js?v=20260706d';
import { apiJson } from '../utils/api_utils.js?v=20260730a';

const DEFAULT_LANGUAGE = 'DE';
const FALLBACK_LANGUAGES = [
  { sprache_code: 'DE', sprache_name: 'Deutsch', flagge_emoji: '🇩🇪', ist_standard: true }
];

let initPromise = null;
let initialized = false;
let availableLanguages = FALLBACK_LANGUAGES;
let i18nHealthHasIssue = false;

function normalizeLanguageCode(code) {
  return String(code || DEFAULT_LANGUAGE).trim().toUpperCase() || DEFAULT_LANGUAGE;
}

function getActiveLanguage() {
  return normalizeLanguageCode(S.ui?.language);
}

function ensureI18nextReady() {
  if (!initPromise) {
    initPromise = i18next.init({
      lng: getActiveLanguage(),
      fallbackLng: false,
      keySeparator: false,
      nsSeparator: false,
      interpolation: { escapeValue: false },
      resources: {}
    }).then(() => {
      initialized = true;
    });
  }
  return initPromise;
}

function availableLanguagesList() {
  return availableLanguages;
}

async function loadAvailableLanguages() {
  try {
    const payload = await apiJson('/api/i18n/languages');
    const languages = Array.isArray(payload.languages) ? payload.languages : [];
    availableLanguages = languages.length ? languages : FALLBACK_LANGUAGES;
  } catch (error) {
    console.warn('KS Studio: Sprachliste konnte nicht geladen werden, verwende Standardsprache.', error);
    availableLanguages = FALLBACK_LANGUAGES;
  }
  return availableLanguages;
}

async function loadActiveLanguageLabels(langCode = getActiveLanguage()) {
  await ensureI18nextReady();
  const code = normalizeLanguageCode(langCode);
  try {
    const payload = await apiJson(`/api/i18n/labels?lang=${encodeURIComponent(code)}`);
    i18next.addResourceBundle(code, 'translation', payload.labels || {}, true, true);
  } catch (error) {
    console.warn('KS Studio: Beschriftungen konnten nicht geladen werden, es bleibt beim hart codierten Text.', error);
  }
}

function hasI18nHealthIssue() {
  return i18nHealthHasIssue;
}

async function loadI18nHealthStatus() {
  try {
    const payload = await apiJson('/api/admin/i18n/health');
    i18nHealthHasIssue = Boolean(payload.hasIssues);
  } catch (error) {
    console.warn('KS Studio: i18n-Health-Status konnte nicht geladen werden.', error);
    i18nHealthHasIssue = false;
  }
  AppRuntime.requestAppRefresh(false, 'i18n-health');
}

async function initI18n() {
  await ensureI18nextReady();
  await Promise.all([loadAvailableLanguages(), loadActiveLanguageLabels()]);
}

async function setActiveLanguage(code) {
  const nextCode = normalizeLanguageCode(code);
  if (!S.ui || typeof S.ui !== 'object') S.ui = {};
  S.ui.language = nextCode;
  await ensureI18nextReady();
  await loadActiveLanguageLabels(nextCode);
  await i18next.changeLanguage(nextCode);
  return nextCode;
}

// Thin wrapper around i18next.t(): fallbackText is both the shown text before
// initI18n() resolves and the value i18next falls back to for any key that
// has no translation yet in the active language (including German itself,
// which — like every other language — lives only in the DB, seeded from
// this exact fallback string at retrofit time; never hard-coded here).
function t(schluessel, fallbackText) {
  if (!initialized) return fallbackText ?? schluessel;
  return i18next.t(schluessel, { defaultValue: fallbackText ?? schluessel });
}

// For the handful of labels that live in static index.html markup (rendered
// once by the browser, never re-generated by a JS template literal — e.g.
// the "Gruppen"/"Objekte" sidebar card titles) rather than in a render()
// function: mark the element with data-i18n-key and call this once after
// initI18n() resolves, and again on every language switch. The very first
// call captures the element's original (German) text as its permanent
// fallback in data-i18n-fallback, so re-running this after a switch back to
// German never re-derives the fallback from already-translated text.
function applyStaticTranslations(root = document) {
  root.querySelectorAll('[data-i18n-key]').forEach(element => {
    if (!element.dataset.i18nFallback) element.dataset.i18nFallback = element.textContent;
    element.textContent = t(element.dataset.i18nKey, element.dataset.i18nFallback);
  });
}

export {
  getActiveLanguage,
  availableLanguagesList,
  loadAvailableLanguages,
  loadActiveLanguageLabels,
  initI18n,
  setActiveLanguage,
  t,
  applyStaticTranslations,
  hasI18nHealthIssue,
  loadI18nHealthStatus
};
